import React, { useEffect, useState } from 'react'
import { Platform } from 'react-native'
import { ArrowLeft, Check } from 'lucide-react-native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import { supabase } from '../lib/supabase'
import { createReservationPaymentIntent, formatCurrency, getStripePublishableKey } from '../lib/stripe'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import {
  Button, ButtonText, Divider, Spinner, Text,
  XStack, YStack, ScrollView,
} from '@fliisi/pollen'

const isWeb = Platform.OS === 'web'
const ENABLE_NATIVE_PAYMENTS = false

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Checkout'>
  route: RouteProp<RootStackParamList, 'Checkout'>
}

interface Reservation {
  id: string
  start_date: string
  end_date: string
  price_total: number
  status: string
  payment_status: string
  space: { name: string; space_type: string }
  market: { name: string }
}

export default function CheckoutScreen({ navigation, route }: Props) {
  const { reservationId } = route.params
  const { t, language } = useLanguage()
  const { user } = useAuth()
  const [reservation, setReservation] = useState<Reservation | null>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [stripe, setStripe] = useState<any>(null)
  const [elements, setElements] = useState<any>(null)
  const [cardElement, setCardElement] = useState<any>(null)
  const [paymentSheetReady, setPaymentSheetReady] = useState(false)

  useEffect(() => {
    fetchReservation()
    if (isWeb) loadStripeJs()
  }, [reservationId])

  useEffect(() => {
    if (ENABLE_NATIVE_PAYMENTS && !isWeb && reservation && !paymentSheetReady && reservation.payment_status !== 'paid') {
      initializeNativePaymentSheet()
    }
  }, [reservation])

  const loadStripeJs = async () => {
    if (typeof window !== 'undefined' && !(window as any).Stripe) {
      const script = document.createElement('script')
      script.src = 'https://js.stripe.com/v3/'
      script.async = true
      script.onload = () => initializeStripe()
      document.body.appendChild(script)
    } else if ((window as any).Stripe) {
      initializeStripe()
    }
  }

  const initializeStripe = () => {
    const publishableKey = process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY
    if (publishableKey && (window as any).Stripe) setStripe((window as any).Stripe(publishableKey))
  }

  const initializeNativePaymentSheet = async () => {
    if (isWeb || !reservation || !ENABLE_NATIVE_PAYMENTS) return
    console.log('Native payment sheet not available - using web flow')
  }

  const handleNativePayment = async () => {
    console.log('Native payment not available - using web flow')
  }

  const fetchReservation = async () => {
    try {
      const { data, error } = await supabase
        .from('space_reservations')
        .select(`id, start_date, end_date, price_total, status, payment_status, space:market_spaces (name, space_type), market:flea_markets (name)`)
        .eq('id', reservationId)
        .single()
      if (error) throw error
      setReservation({
        id: data.id, start_date: data.start_date, end_date: data.end_date,
        price_total: data.price_total, status: data.status, payment_status: data.payment_status,
        space: Array.isArray(data.space) ? data.space[0] : data.space,
        market: Array.isArray(data.market) ? data.market[0] : data.market,
      })
      if (data.payment_status === 'paid') setError(t('already_paid') || 'This reservation has already been paid')
    } catch (err: any) {
      console.error('Error fetching reservation:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const initializePayment = async () => {
    if (!stripe) { setError(t('stripe_not_loaded') || 'Payment system not loaded. Please refresh.'); return }
    setProcessing(true); setError(null)
    try {
      const result = await createReservationPaymentIntent(reservationId)
      if (result.error) throw new Error(result.error)
      setClientSecret(result.clientSecret)
      const elementsInstance = stripe.elements({
        clientSecret: result.clientSecret,
        appearance: { theme: 'stripe', variables: { colorPrimary: '#748C70', borderRadius: '8px' } },
      })
      setElements(elementsInstance)
      const card = elementsInstance.create('payment')
      setCardElement(card)
      setTimeout(() => { document.getElementById('card-element')?.parentElement && card.mount('#card-element') }, 100)
    } catch (err: any) {
      console.error('Error initializing payment:', err)
      setError(err.message)
    } finally {
      setProcessing(false)
    }
  }

  const handlePayment = async () => {
    if (!stripe || !elements || !clientSecret) { setError(t('payment_not_ready') || 'Payment not ready. Please try again.'); return }
    setProcessing(true); setError(null)
    try {
      const { error: submitError } = await elements.submit()
      if (submitError) throw new Error(submitError.message)
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements, clientSecret,
        confirmParams: { return_url: `${window.location.origin}/reservation/${reservationId}/success` },
        redirect: 'if_required',
      })
      if (confirmError) throw new Error(confirmError.message)
      if (paymentIntent?.status === 'succeeded') { setPaymentSuccess(true); await fetchReservation() }
    } catch (err: any) {
      console.error('Payment error:', err)
      setError(err.message)
    } finally {
      setProcessing(false)
    }
  }

  const formatDate = (dateStr: string) => {
    const locale = language === 'fi' ? 'fi-FI' : 'en-US'
    return new Date(dateStr).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })
  }

  const getDays = () => {
    if (!reservation) return 0
    const diffTime = Math.abs(new Date(reservation.end_date).getTime() - new Date(reservation.start_date).getTime())
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
  }

  const HeaderBar = ({ title }: { title: string }) => (
    <XStack jc="space-between" ai="center" px="$md" pt="$md" pb="$md" bg="$backgroundCard" bbw={1} bbc="$borderColor">
      <YStack
        py={8} pr="$md" onPress={() => navigation.goBack()}
        pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
      >
        <XStack ai="center" gap="$xs">
          <ArrowLeft size={18} color="$colorPrimary" />
          <Text fontSize={16} color="$colorPrimary" fontWeight="500">{t('back') || 'Back'}</Text>
        </XStack>
      </YStack>
      <Text fontSize={18} fontWeight="bold" color="$color">{title}</Text>
      <YStack w={60} />
    </XStack>
  )

  if (loading) {
    return (
      <YStack f={1} bg="$background">
        <HeaderBar title={t('checkout') || 'Checkout'} />
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      </YStack>
    )
  }

  if (paymentSuccess) {
    return (
      <YStack f={1} bg="$background">
        <XStack jc="center" ai="center" px="$md" pt="$md" pb="$md" bg="$backgroundCard" bbw={1} bbc="$borderColor">
          <Text fontSize={18} fontWeight="bold" color="$color">{t('payment_successful') || 'Payment Successful'}</Text>
        </XStack>
        <YStack f={1} jc="center" ai="center" p="$xl">
          <YStack w={80} h={80} br={40} bg="#E8F5E9" jc="center" ai="center" mb="$md">
            <Check size={48} color="#4CAF50" />
          </YStack>
          <Text fontSize={28} fontWeight="bold" color="$color" mb="$sm">{t('thank_you') || 'Thank You!'}</Text>
          <Text fontSize={16} color="$colorSecondary" textAlign="center" mb="$xl">
            {t('reservation_confirmed') || 'Your reservation has been confirmed.'}
          </Text>
          <YStack bg="$backgroundSecondary" p="$lg" br="$lg" ai="center" mb="$xl" w="100%" maxWidth={300}>
            <Text fontSize={18} fontWeight="600" color="$color">{reservation?.space?.name}</Text>
            <Text fontSize={14} color="$colorSecondary" mt={4}>{reservation?.market?.name}</Text>
            <Text fontSize={14} color="$colorPrimary" fontWeight="500" mt={8}>
              {formatDate(reservation?.start_date || '')} - {formatDate(reservation?.end_date || '')}
            </Text>
          </YStack>
          <Button variant="primary" px="$2xl" py="$md" onPress={() => navigation.navigate('JoinMarket')}>
            <ButtonText fontSize={18}>{t('done') || 'Done'}</ButtonText>
          </Button>
        </YStack>
      </YStack>
    )
  }

  return (
    <YStack f={1} bg="$background">
      <HeaderBar title={t('checkout') || 'Checkout'} />

      <ScrollView f={1} p="$md">
        {/* Reservation Summary */}
        <YStack bg="$backgroundCard" br="$lg" p="$md" mb="$md" bw={1} bbc="$borderColor">
          <Text fontSize={18} fontWeight="600" color="$color" mb="$md">{t('reservation_summary') || 'Reservation Summary'}</Text>

          {[
            { label: t('space') || 'Space', value: reservation?.space?.name },
            { label: t('market') || 'Market', value: reservation?.market?.name },
            { label: t('dates') || 'Dates', value: `${formatDate(reservation?.start_date || '')} - ${formatDate(reservation?.end_date || '')}` },
            { label: t('duration') || 'Duration', value: `${getDays()} ${t('days') || 'days'}` },
          ].map((row) => (
            <XStack key={row.label} jc="space-between" py={8}>
              <Text fontSize={14} color="$colorSecondary">{row.label}</Text>
              <Text fontSize={14} fontWeight="500" color="$color">{row.value}</Text>
            </XStack>
          ))}

          <Divider my="$md" />

          <XStack jc="space-between" ai="center">
            <Text fontSize={18} fontWeight="600" color="$color">{t('total') || 'Total'}</Text>
            <Text fontSize={24} fontWeight="bold" color="$colorPrimary">{formatCurrency(reservation?.price_total || 0)}</Text>
          </XStack>
        </YStack>

        {/* Payment Section */}
        <YStack bg="$backgroundCard" br="$lg" p="$md" bw={1} bbc="$borderColor">
          <Text fontSize={18} fontWeight="600" color="$color" mb="$md">{t('payment') || 'Payment'}</Text>

          {error && (
            <YStack bg="#FFEBEE" p="$md" br="$sm" mb="$md">
              <Text color="#C62828" fontSize={14}>{error}</Text>
            </YStack>
          )}

          {!clientSecret ? (
            <Button variant="primary" onPress={initializePayment} disabled={processing} opacity={processing ? 0.6 : 1}>
              {processing ? <Spinner color="#fff" /> : <ButtonText fontSize={18}>{t('proceed_to_payment') || 'Proceed to Payment'}</ButtonText>}
            </Button>
          ) : (
            <>
              {isWeb && (
                <YStack bg="$backgroundSecondary" br="$sm" p="$md" mb="$md" minHeight={50}>
                  <div id="card-element" style={{ minHeight: 40 }} />
                </YStack>
              )}
              <Button variant="primary" onPress={handlePayment} disabled={processing} opacity={processing ? 0.6 : 1}>
                {processing ? <Spinner color="#fff" /> : <ButtonText fontSize={18}>{t('pay')} {formatCurrency(reservation?.price_total || 0)}</ButtonText>}
              </Button>
            </>
          )}

          <XStack jc="center" ai="center" mt="$md" gap="$sm">
            <Text fontSize={16}>🔒</Text>
            <Text fontSize={12} color="$colorSecondary">{t('secure_payment') || 'Secure payment powered by Stripe'}</Text>
          </XStack>
        </YStack>

        <YStack h={40} />
      </ScrollView>
    </YStack>
  )
}
